// Who owns a heavy content body (TODO §21/§4.3.3, anticipating §17.1.3).
//
// Every out-of-band body — a grid, a diagram, an image blob — is filed in IndexedDB under
// the id of the thing that owns it, and until now that was always a deck id. §17 (knowledge
// nuggets) introduces a second owner: a nugget, whose body is shared by every deck that
// links the nugget, and which therefore must NOT be deleted when one of those decks drops
// the slide.
//
// Diagrams are the FIRST store written with this helper, and that is deliberate: §21 is
// scheduled around it. Retrofitting an owner key onto rows already sitting in customers'
// IndexedDB is a data migration; writing one from the first commit costs a function call.
// So `diagramDocs.owner` holds an owner KEY, never a raw deck id.
//
// The encoding is chosen so the two cases can never be confused: a deck id is a
// `crypto.randomUUID()`, which contains no colon, so the `nugget:` prefix is unambiguous
// and `parseOwnerKey` is total.

export type ContentOwner =
  { kind: "deck"; deckId: string } | { kind: "nugget"; nuggetId: string };

const NUGGET_PREFIX = "nugget:";

export function ownerKey(scope: ContentOwner): string {
  return scope.kind === "deck"
    ? scope.deckId
    : `${NUGGET_PREFIX}${scope.nuggetId}`;
}

// The common case, named so call sites read as intent rather than as a shape.
export function deckOwner(deckId: string): string {
  return ownerKey({ kind: "deck", deckId });
}

// §17.1.3 The other case, now that it exists: a knowledge nugget's own bodies and blobs,
// filed under a key no deck id can collide with and no deck's cascade delete can reach.
export function nuggetOwner(nuggetId: string): string {
  return ownerKey({ kind: "nugget", nuggetId });
}

export function parseOwnerKey(key: string): ContentOwner {
  return key.startsWith(NUGGET_PREFIX)
    ? { kind: "nugget", nuggetId: key.slice(NUGGET_PREFIX.length) }
    : { kind: "deck", deckId: key };
}

// §17.6.2's question, asked in one place: may THIS deck delete a body outright, or must it
// only unlink it? A nugget-owned body is shared, so dropping it is data loss across every
// deck that links the nugget.
export function ownedByDeck(key: string, deckId: string): boolean {
  return key === deckOwner(deckId);
}
